import PptxGenJS from "pptxgenjs";
import { calculateGlobalNorm } from "./slide2";

export const slide10 = (pptx: PptxGenJS, data: any) => {
    const reportData = data?.data;
    const combinedQuestions = reportData?.participantReport?.combinedQuestions || [];
    const candidateName = data?.candidateName || 'Jane Doe';

    // Filter out comments and invalid scores
    const behaviors = combinedQuestions.filter((q: any) => q.tag !== "Comments" && q.my_score !== null);

    // Chunking function
    const chunkArray = (arr: any[], size: number) => {
        const chunked = [];
        for (let i = 0; i < arr.length; i += size) {
            chunked.push(arr.slice(i, i + size));
        }
        return chunked;
    };

    const behaviorChunks = chunkArray(behaviors, 3);
    let currentPageNum = 10;

    behaviorChunks.forEach((chunk, chunkIndex) => {
        const slide = pptx.addSlide();

        // Logo
        slide.addImage({
            path: '/Access-Holdings-Logo.jpg',
            x: 0.5,
            y: 0.2,
            w: 1.3,
            h: 0.45
        });

        // Domain Title
        slide.addText("Executor", {
            x: 0.5,
            y: 0.8,
            w: 7.27,
            h: 0.4,
            fontSize: 24,
            bold: true,
            color: '000000'
        });

        // Domain Description (Only on the first slide of the domain?)
        // User's original code had it on slide 10. Let's keep it on every slide for consistency if it fits.
        slide.addText("The Executor domain focuses on driving results through effective change management, decision-making, accountability, team building, and technical proficiency. Leaders strong in this domain ensure organizational objectives are achieved efficiently and effectively.", {
            x: 0.5,
            y: 1.2,
            w: 7.27,
            h: 0.6,
            fontSize: 10,
            color: '404040'
        });

        let currentY = 1.8;

        chunk.forEach((q, qIndex) => {
            const competencyTitle = q.tag;
            const behaviorText = q.text;

            // Orange circle with label (A, B, C...)
            // If we want unique letters across all slides, we should use behavior index
            const behaviorIdx = chunkIndex * 3 + qIndex;
            const label = String.fromCharCode(65 + (behaviorIdx % 26)); // A, B, C...

            slide.addShape('ellipse', {
                x: 0.5,
                y: currentY + 0.05,
                w: 0.2,
                h: 0.2,
                fill: { color: 'FF6600' }
            });
            slide.addText(label, {
                x: 0.5,
                y: currentY + 0.05,
                w: 0.2,
                h: 0.2,
                fontSize: 8,
                bold: true,
                color: 'FFFFFF',
                align: 'center',
                valign: 'middle'
            });

            slide.addText(competencyTitle, {
                x: 0.8,
                y: currentY,
                w: 4,
                h: 0.3,
                fontSize: 14,
                bold: true,
                color: 'FF6600'
            });

            currentY += 0.4;

            // Prepare Table Data
            const globalNormScore = calculateGlobalNorm(q.raters);

            const tableRows = [];

            // Total Row
            let totalN = 0;
            let totalSDSum = 0;
            q.raters.forEach((r: any) => {
                totalN += r.rater_count;
                totalSDSum += (r.variance || 0) * r.rater_count;
            });
            const avgSD = totalN > 0 ? totalSDSum / totalN : 0;

            tableRows.push({
                label: "Total",
                score: globalNormScore,
                norm: globalNormScore, // Using same norm for all rows per question
                n: totalN,
                sd: avgSD,
                type: 'total'
            });

            // Self Row
            tableRows.push({
                label: "Self",
                score: q.my_score || 0,
                norm: globalNormScore,
                n: 1,
                sd: 0,
                type: 'self'
            });

            // Rater Group Rows
            q.raters.forEach((r: any) => {
                tableRows.push({
                    label: r.review_type.endsWith('s') ? r.review_type : r.review_type + 's',
                    score: r.avg_score,
                    norm: globalNormScore,
                    n: r.rater_count,
                    sd: r.variance,
                    type: 'other'
                });
            });

            // Helper to add the breakdown table
            const addBreakdownTableInternal = (s: any, top: number, title: string, rows: any[]) => {
                let y = top;

                s.addText(title, {
                    x: 0.5,
                    y: y,
                    w: 7.27,
                    h: 0.3,
                    fontSize: 11,
                    bold: true,
                    color: '404040'
                });
                y += 0.35;

                const colW = [1.5, 0.6, 0.6, 4.0, 0.4, 0.4];
                const startX = 0.5;
                const rowH = 0.35;
                const headerH = 0.5;

                // Headers
                const headers = ["", "SCORE", "GLOBAL\nNORM", "", "N", "SD"];
                headers.forEach((h, i) => {
                    let x = startX + colW.slice(0, i).reduce((a, b) => a + b, 0);
                    s.addShape('rect', { x: x, y: y, w: colW[i], h: headerH, line: { color: 'D0D0D0', width: 0.5 } });
                    if (h) {
                        s.addText(h, {
                            x: x, y: y, w: colW[i], h: headerH,
                            fontSize: 7, bold: true, align: 'center', valign: 'middle', color: '000000'
                        });
                    }
                });
                y += headerH;

                rows.forEach((r) => {
                    let rowStartX = startX;
                    // Name cell
                    s.addShape('rect', { x: rowStartX, y: y, w: colW[0], h: rowH, line: { color: 'D0D0D0', width: 0.5 } });
                    s.addText(r.label, {
                        x: rowStartX + 0.1, y: y, w: colW[0] - 0.1, h: rowH,
                        fontSize: 9, bold: r.type === 'total', valign: 'middle'
                    });
                    rowStartX += colW[0];

                    // Score cell
                    s.addShape('rect', { x: rowStartX, y: y, w: colW[1], h: rowH, line: { color: 'D0D0D0', width: 0.5 } });
                    s.addText(r.score.toFixed(2), { x: rowStartX, y: y, w: colW[1], h: rowH, fontSize: 9, align: 'center', valign: 'middle' });
                    rowStartX += colW[1];

                    // Global Norm cell
                    s.addShape('rect', { x: rowStartX, y: y, w: colW[2], h: rowH, line: { color: 'D0D0D0', width: 0.5 } });
                    s.addText(r.norm.toFixed(2), { x: rowStartX, y: y, w: colW[2], h: rowH, fontSize: 9, align: 'center', valign: 'middle', color: '808080' });
                    rowStartX += colW[2];

                    // Bar Chart cell
                    s.addShape('rect', { x: rowStartX, y: y, w: colW[3], h: rowH, line: { color: 'D0D0D0', width: 0.5 } });

                    // Draw Bar
                    const barMaxWidth = colW[3] - 0.4;
                    const barX = rowStartX + 0.2;
                    const barY = y + (rowH / 2) - 0.035;
                    const scale = 5;
                    const barWidth = (Math.min(r.score, scale) / scale) * barMaxWidth;
                    const normX = (Math.min(r.norm, scale) / scale) * barMaxWidth;

                    let barColor = 'FF6600';
                    if (r.type === 'total') barColor = '000000';
                    //if (r.type === 'self') barColor = 'FF6600';

                    // Background of bar
                    s.addShape('rect', {
                        x: barX, y: barY, w: barMaxWidth, h: 0.07,
                        fill: { color: 'F0F0F0' }
                    });

                    // Actual Score Bar
                    s.addShape('rect', {
                        x: barX, y: barY, w: Math.max(0, barWidth), h: 0.07,
                        fill: { color: barColor }
                    });

                    // Global Norm Tick
                    const tickColor = r.type === 'total' ? 'FF6600' : '000000';
                    s.addShape('rect', {
                        x: barX + normX, y: barY - 0.1, w: 0.02, h: 0.27,
                        fill: { color: tickColor }
                    });

                    rowStartX += colW[3];

                    // N cell
                    s.addShape('rect', { x: rowStartX, y: y, w: colW[4], h: rowH, line: { color: 'D0D0D0', width: 0.5 } });
                    s.addText(r.n.toString(), { x: rowStartX, y: y, w: colW[4], h: rowH, fontSize: 8, align: 'center', valign: 'middle', color: '808080' });
                    rowStartX += colW[4];

                    // SD cell
                    s.addShape('rect', { x: rowStartX, y: y, w: colW[5], h: rowH, line: { color: 'D0D0D0', width: 0.5 } });
                    s.addText(r.sd.toFixed(2), { x: rowStartX, y: y, w: colW[5], h: rowH, fontSize: 8, align: 'center', valign: 'middle', color: '808080' });

                    y += rowH;
                });

                return y;
            };

            currentY = addBreakdownTableInternal(slide, currentY, behaviorText, tableRows);
            currentY += 0.3; // Space between tables
        });

        // Footer
        slide.addText(`360-Degree Feedback Individual Report for ${candidateName}`, {
            x: 0,
            y: 11.4,
            w: 7.8,
            h: 0.3,
            fontSize: 9,
            color: '666666',
            align: 'center'
        });

        // slide.addText(`${currentPageNum}`, {
        //     x: 7.8,
        //     y: 11.2,
        //     w: 0.4,
        //     h: 0.3,
        //     fontSize: 9,
        //     color: '666666',
        //     align: 'center'
        // });

        currentPageNum++;
    });
};